import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Users,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Clock,
} from "lucide-react";

const STATUS_COLORS = {
  Active: "#30A46C",
  Expiring: "#F5D90A",
  Expired: "#E5484D",
  Pending: "#A1A1AA",
};

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="glass-card rounded-lg p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
          {label}
        </p>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="kpi-num text-3xl sm:text-4xl font-black tracking-tight mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setStats(data));
  }, []);

  if (!stats) {
    return <div data-testid="dash-loading" className="text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Real-time overview of your premiumtools.store business.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Kpi testid="kpi-total-users" label="Total Users" value={stats.total_users} icon={Users} accent="bg-foreground/5 text-foreground" />
        <Kpi testid="kpi-active" label="Active" value={stats.active_subscriptions} icon={CheckCircle2} accent="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" />
        <Kpi testid="kpi-expiring" label="Expiring ≤7d" value={stats.expiring_soon} icon={CalendarClock} accent="bg-yellow-400/15 text-yellow-700 dark:text-yellow-400" />
        <Kpi testid="kpi-expired" label="Expired" value={stats.expired} icon={AlertTriangle} accent="bg-red-500/15 text-red-600 dark:text-red-400" />
        <Kpi testid="kpi-pending-pay" label="Pending Pay" value={stats.pending_payments} icon={Clock} accent="bg-blue-500/15 text-blue-600 dark:text-blue-400" />
        <Kpi testid="kpi-revenue" label="Revenue (mo)" value={`৳${stats.monthly_revenue.toFixed(0)}`} icon={DollarSign} accent="bg-foreground/5 text-foreground" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
        <div className="glass-card rounded-lg p-5 lg:col-span-4" data-testid="revenue-chart">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            Revenue · last 6 months
          </p>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.revenue_chart}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                <XAxis dataKey="month" stroke="currentColor" fontSize={12} tickLine={false} />
                <YAxis stroke="currentColor" fontSize={12} tickLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                <Bar dataKey="revenue" fill="hsl(var(--foreground))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card rounded-lg p-5 lg:col-span-2" data-testid="status-chart">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            Subscription status
          </p>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.status_chart} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {stats.status_chart.map((s, i) => (
                    <Cell key={i} fill={STATUS_COLORS[s.name] || "#A1A1AA"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="glass-card rounded-lg p-5" data-testid="expiring-list">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            Expiring Soon / Overdue
          </p>
        </div>
        <div className="mt-4 divide-y divide-border/40">
          {stats.expiring_list.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Kono subscription expiring nei. All clear.
            </p>
          )}
          {stats.expiring_list.map((s) => {
            const overdue = s.days_left != null && s.days_left < 0;
            return (
              <div
                key={s.id}
                className="flex items-center justify-between py-3"
                data-testid={`exp-${s.id}`}
              >
                <div>
                  <p className="font-medium text-sm">{s.user?.name || "—"}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {s.plan?.name} · {s.user?.email}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-mono">{s.expire_date?.slice(0, 10)}</p>
                  <p className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${overdue ? "text-red-500" : "text-yellow-600 dark:text-yellow-400"}`}>
                    {overdue ? `${-s.days_left}d OVERDUE` : `${s.days_left}d left`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
